import { TwitterApiTweetAuthorSchema, TwitterApiTweetSchema } from '@sweetistics/contracts';
import type { TweetLinkMetadata } from '@sweetistics/contracts/tweets-canonical';
import type { z } from 'zod';
import type { Tweet } from '../../../types/tweet';
import {
  ContextAnnotationSchema,
  ReferencedTweetSchema,
  TweetAttachmentsSchema,
  TweetEntitiesSchema,
} from '../../../types/tweet';
import { MediaItemSchema, MediaMetadataEntrySchema } from '../../../types/tweet/media';
import { toCanonicalLinkMetadataRecord } from '../../url-metadata/normalize';
import { getRecordValue, isRecord } from '../../utils/type-guards';
import { type ReplySettings, ReplySettingsCodec } from '../codecs';

// Shared boundary schema for tweets returned by API endpoints
// Transforms ISO date strings to Date and validates nested structures

// Keep author schema exactly aligned with TweetAuthor while decoding dates from ISO
const TweetAuthorFromApiSchema = TwitterApiTweetAuthorSchema.transform((author) => ({
  id: author.id,
  username: author.username,
  name: author.name,
  profileImageUrl: author.profileImageUrl ?? undefined,
  verifiedType: author.verifiedType,
  createdAt: author.createdAt,
  description: author.description,
  publicMetrics: author.publicMetrics,
  verified: author.verified,
}));

type TweetApiInput = z.infer<typeof TwitterApiTweetSchema>;

const normalizeMetadataRecord = (value: unknown): Record<string, TweetLinkMetadata> | undefined =>
  toCanonicalLinkMetadataRecord(value);

const normalizeReplySettings = (value: unknown): ReplySettings | undefined => {
  if (typeof value !== 'string') {
    return;
  }
  try {
    return ReplySettingsCodec.decode(value);
  } catch {
    return;
  }
};

/**
 * Remote tweet payloads occasionally omit the `description` field on URL
 * entities. Our internal schema (and downstream contracts) rely on an
 * explicit `null`, so pre-fill that default before handing the value to
 * Zod to avoid noisy validation errors.
 */
function sanitizeTweetEntitiesInput(entities: unknown): unknown {
  if (!isRecord(entities)) {
    return entities;
  }
  // biome-ignore lint/complexity/useLiteralKeys: entities is a loose record from external APIs.
  const rawUrls = entities['urls'];
  if (!Array.isArray(rawUrls) || rawUrls.length === 0) {
    return entities;
  }

  const urlEntries: unknown[] = rawUrls;

  let mutated = false;
  const sanitizedUrls = urlEntries.map((entry) => {
    if (!isRecord(entry)) {
      return entry;
    }
    const record = entry;
    if (getRecordValue(record, 'description') === undefined) {
      mutated = true;
      return { ...record, description: null };
    }
    return record;
  });

  if (!mutated) {
    return entities;
  }

  return {
    ...entities,
    urls: sanitizedUrls,
  } satisfies Record<string, unknown>;
}

const transformTweetFromApi = (tweet: TweetApiInput): Tweet => {
  const mediaItems = Array.isArray(tweet.mediaItems)
    ? tweet.mediaItems.map((item) => MediaItemSchema.parse(item))
    : undefined;

  const contextAnnotations = Array.isArray(tweet.contextAnnotations)
    ? (() => {
        const annotations: NonNullable<Tweet['contextAnnotations']> = [];
        for (const item of tweet.contextAnnotations) {
          const parsed = ContextAnnotationSchema.safeParse(item);
          if (parsed.success) {
            annotations.push(parsed.data);
          }
        }
        return annotations;
      })()
    : undefined;

  const metrics = {
    likeCount: tweet.likeCount ?? 0,
    retweetCount: tweet.retweetCount ?? 0,
    replyCount: tweet.replyCount ?? 0,
    quoteCount: tweet.quoteCount ?? 0,
    bookmarkCount: tweet.bookmarkCount ?? undefined,
    impressionCount: tweet.impressionCount ?? undefined,
  };

  return {
    id: tweet.id,
    text: tweet.text,
    authorId: tweet.authorId,
    createdAt: tweet.createdAt,
    metrics,
    likeCount: metrics.likeCount,
    retweetCount: metrics.retweetCount,
    replyCount: metrics.replyCount,
    quoteCount: metrics.quoteCount,
    bookmarkCount: metrics.bookmarkCount ?? undefined,
    impressionCount: metrics.impressionCount ?? undefined,
    author: tweet.author ? TweetAuthorFromApiSchema.parse(tweet.author) : undefined,
    conversationId: tweet.conversationId,
    inReplyToUserId: tweet.inReplyToUserId,
    lang: tweet.lang,
    entities: tweet.entities ? TweetEntitiesSchema.parse(sanitizeTweetEntitiesInput(tweet.entities)) : undefined,
    attachments: tweet.attachments ? TweetAttachmentsSchema.parse(tweet.attachments) : undefined,
    mediaItems,
    referencedTweets: tweet.referencedTweets
      ? tweet.referencedTweets.map((item) => ReferencedTweetSchema.parse(item))
      : undefined,
    quotedTweet: tweet.quotedTweet ? transformTweetFromApi(tweet.quotedTweet) : undefined,
    retweetedTweet: tweet.retweetedTweet ? transformTweetFromApi(tweet.retweetedTweet) : undefined,
    isLiked: tweet.isLiked,
    isRetweeted: tweet.isRetweeted,
    isLikedByUser: tweet.isLikedByUser,
    isPinned: tweet.isPinned,
    relevanceScore: tweet.relevanceScore,
    editHistoryTweetIds: tweet.editHistoryTweetIds,
    contextAnnotations,
    possiblySensitive: tweet.possiblySensitive,
    replySettings: normalizeReplySettings(tweet.replySettings),
    metadata: normalizeMetadataRecord(tweet.metadata),
    mediaMetadata: tweet.mediaMetadata
      ? tweet.mediaMetadata.map((entry) => MediaMetadataEntrySchema.parse(entry))
      : undefined,
  };
};

export const TweetFromApiSchema = TwitterApiTweetSchema.transform<Tweet>(transformTweetFromApi);

export const tweetApi = {
  parse(input: unknown): Tweet {
    return TweetFromApiSchema.parse(input);
  },
  safeParse(input: unknown) {
    return TweetFromApiSchema.safeParse(input);
  },
};
